import { initializeApp, getApps, getApp } from 'firebase/app';
import { getAuth } from 'firebase/auth';
import { getFirestore } from 'firebase/firestore';
import { getFunctions } from 'firebase/functions';
import { getStorage } from 'firebase/storage';
import { getAnalytics } from 'firebase/analytics';

// Your web app's Firebase configuration from environment variables
const firebaseConfig = {
  apiKey: import.meta.env.VITE_FIREBASE_API_KEY,
  authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN,
  projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID,
  storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID,
  appId: import.meta.env.VITE_FIREBASE_APP_ID,
  measurementId: import.meta.env.VITE_FIREBASE_MEASUREMENT_ID
};

// Validate Firebase configuration for both development and production
// This prevents the "invalid-api-key" error by catching missing environment variables early
const requiredFields = ['apiKey', 'authDomain', 'projectId', 'storageBucket', 'messagingSenderId', 'appId'];
const missingFields = requiredFields.filter(field => !firebaseConfig[field as keyof typeof firebaseConfig]);

if (missingFields.length > 0) {
  const isDev = import.meta.env.DEV;
  const errorContext = isDev 
    ? 'For local development: Create a .env file in the web/ directory with the following variables:'
    : 'For production: GitHub Secrets must be configured in your repository settings.';
    
  console.error('❌ Missing Firebase configuration:', missingFields);
  console.error('📝 ' + errorContext);
  console.error('   VITE_FIREBASE_API_KEY=your_api_key');
  console.error('   VITE_FIREBASE_AUTH_DOMAIN=your_auth_domain');
  console.error('   VITE_FIREBASE_PROJECT_ID=your_project_id');
  console.error('   VITE_FIREBASE_STORAGE_BUCKET=your_storage_bucket');
  console.error('   VITE_FIREBASE_MESSAGING_SENDER_ID=your_sender_id');
  console.error('   VITE_FIREBASE_APP_ID=your_app_id');
  console.error('   VITE_FIREBASE_MEASUREMENT_ID=your_measurement_id');
  
  if (!isDev) {
    console.error('');
    console.error('🔧 To fix this issue:');
    console.error('1. Go to: https://github.com/[YOUR_USERNAME]/WaitlistPoker/settings/secrets/actions');
    console.error('2. Add each VITE_FIREBASE_* secret');
    console.error('3. Redeploy the application');
  }
  
  throw new Error(`Missing required Firebase configuration: ${missingFields.join(', ')}`);
}

// Guard initialization with getApps() to avoid multiple app instances
const app = getApps().length === 0 ? initializeApp(firebaseConfig) : getApp();

// Initialize Firebase services with explicit typing to prevent minification issues
export const auth = getAuth(app);
export const db = getFirestore(app) as any; // Force type preservation
export const functions = getFunctions(app);
export const storage = getStorage(app);

// Initialize Analytics conditionally to prevent errors
let analytics: any = null;
try {
  // Only initialize analytics in browser environment and when supported
  if (typeof window !== 'undefined' && window.location.protocol === 'https:') {
    analytics = getAnalytics(app);
  }
} catch (error) {
  console.warn('Firebase Analytics initialization failed:', error);
}
export { analytics };


// Create a wrapper function that ensures proper Firebase types
export const getFirestoreDb = () => {
  const firestoreDb = getFirestore(app);
  return firestoreDb;
};

// Expose globally for shared modules
if (typeof window !== 'undefined') {
  (window as any).getFirestoreDb = getFirestoreDb;
}

// Connect to emulators in development (disabled for now)
// Uncomment and configure firebase.json to use emulators
/*
if ((import.meta as any).env?.DEV) {
  // Only connect to emulators if not already connected
  try {
    connectAuthEmulator(auth, 'http://localhost:9099');
  } catch {
    // Already connected or emulator not available
  }
  
  try {
    connectFirestoreEmulator(db, 'localhost', 8080);
  } catch {
    // Already connected or emulator not available
  }
  
  try {
    connectFunctionsEmulator(functions, 'localhost', 5001);
  } catch {
    // Already connected or emulator not available
  }
}
*/

export default app;
